/**
 * Offline Queue — IndexedDB wrapper (POS + Back-office)
 * ------------------------------------------------------
 * Zero-dep persistence for documents recorded while the browser is offline.
 * Each entry gets a stable client UUID so the backend flush endpoints can
 * de-dupe replays.
 *
 * Object stores:
 *   pos_queue          — POS bills queued at the register while offline
 *     keyPath: client_uuid, indexed by queued_at
 *
 *   back_office_queue  — Back-office docs (GRN, stock entries, wastage,
 *                        transfers) captured on flaky connections
 *     keyPath: client_uuid, indexed by queued_at + doc_type
 */
const DB_NAME = "ssv_pos_offline";
const DB_VERSION = 4;
const STORE_POS = "pos_queue";
const STORE_BO = "back_office_queue";
const STORE_CACHE = "pos_device_cache";

export function openOfflineDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE_POS)) {
        const os = db.createObjectStore(STORE_POS, { keyPath: "client_uuid" });
        os.createIndex("queued_at", "queued_at", { unique: false });
      }
      if (!db.objectStoreNames.contains(STORE_BO)) {
        const os = db.createObjectStore(STORE_BO, { keyPath: "client_uuid" });
        os.createIndex("queued_at", "queued_at", { unique: false });
        os.createIndex("doc_type", "doc_type", { unique: false });
      }
      if (!db.objectStoreNames.contains(STORE_CACHE)) {
        db.createObjectStore(STORE_CACHE, { keyPath: "key" });
      }
      if (!db.objectStoreNames.contains("pos_held_requests")) {
        const store = db.createObjectStore("pos_held_requests", { keyPath: "id" });
        store.createIndex("pending_slot", "pending_slot", { unique: true });
      }
    };
    req.onsuccess = (e) => {
      const db = e.target.result;
      db.onversionchange = () => db.close();
      resolve(db);
    };
    req.onerror = (e) => reject(e.target.error);
  });
}

const openDB = openOfflineDB;

export function uuid() {
  // RFC4122 v4-ish — good enough for idempotency; crypto.randomUUID when available
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// ---------- POS queue (existing) ----------
export async function enqueueOfflineOrder(payload, ownerId = null) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_POS, "readwrite");
    const record = {
      client_uuid: payload.client_uuid || uuid(),
      queued_at: new Date().toISOString(),
      owner_id: ownerId,
      payload: { ...payload },
    };
    record.payload.client_uuid = record.client_uuid;
    record.payload.offline_at ||= record.queued_at;
    tx.objectStore(STORE_POS).add(record);
    tx.oncomplete = () => {
      db.close();
      window.dispatchEvent(new CustomEvent("pos-queue-updated"));
      resolve(record);
    };
    tx.onerror = (e) => reject(e.target.error);
  });
}

export async function listOfflineOrders() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_POS, "readonly");
    const req = tx.objectStore(STORE_POS).getAll();
    tx.oncomplete = () => db.close();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = (e) => reject(e.target.error);
  });
}

export async function countOfflineOrders() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_POS, "readonly");
    const req = tx.objectStore(STORE_POS).count();
    tx.oncomplete = () => db.close();
    req.onsuccess = () => resolve(req.result || 0);
    req.onerror = (e) => reject(e.target.error);
  });
}

export async function removeOfflineOrders(client_uuids) {
  if (!client_uuids || client_uuids.length === 0) return;
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_POS, "readwrite");
    const store = tx.objectStore(STORE_POS);
    client_uuids.forEach((id) => store.delete(id));
    tx.oncomplete = () => {
      db.close();
      window.dispatchEvent(new CustomEvent("pos-queue-updated"));
      resolve();
    };
    tx.onerror = (e) => reject(e.target.error);
  });
}

export async function clearOfflineQueue() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_POS, "readwrite");
    tx.objectStore(STORE_POS).clear();
    tx.oncomplete = () => resolve();
    tx.onerror = (e) => reject(e.target.error);
  });
}

export async function savePOSCache(key, value) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_CACHE, "readwrite");
    tx.objectStore(STORE_CACHE).put({ key, value, updated_at: new Date().toISOString() });
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = (event) => { db.close(); reject(event.target.error); };
  });
}

export async function getPOSCache(key) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_CACHE, "readonly");
    const request = tx.objectStore(STORE_CACHE).get(key);
    request.onsuccess = () => resolve(request.result?.value ?? null);
    request.onerror = (event) => reject(event.target.error);
    tx.oncomplete = () => db.close();
  });
}

export async function setOfflineOrderError(clientUuid, error) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_POS, "readwrite");
    const store = tx.objectStore(STORE_POS);
    const request = store.get(clientUuid);
    request.onsuccess = () => {
      if (request.result) store.put({ ...request.result, sync_error: error });
    };
    tx.oncomplete = () => { db.close(); window.dispatchEvent(new CustomEvent("pos-queue-updated")); resolve(); };
    tx.onerror = (event) => { db.close(); reject(event.target.error); };
  });
}

// ---------- Back-office queue ----------
/**
 * Enqueue a back-office document (GRN, stock_entry, wastage, transfer).
 * doc_type — one of: "grn", "stock_entry", "wastage", "transfer"
 * payload  — the exact JSON the backend would have received online.
 * label    — short human-readable descriptor for the sync UI ("GRN Vendor Ram")
 */
export async function enqueueBackOfficeDoc(doc_type, payload, label = "") {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_BO, "readwrite");
    const record = {
      client_uuid: uuid(),
      doc_type,
      label,
      queued_at: new Date().toISOString(),
      payload,
    };
    tx.objectStore(STORE_BO).add(record);
    tx.oncomplete = () => {
      try { window.dispatchEvent(new CustomEvent("bo-queue-updated")); } catch (_e) { /* Queue write is complete; notification is best-effort outside a browser. */ }
      resolve(record);
    };
    tx.onerror = (e) => reject(e.target.error);
  });
}

export async function listBackOfficeDocs() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_BO, "readonly");
    const req = tx.objectStore(STORE_BO).getAll();
    tx.oncomplete = () => db.close();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = (e) => reject(e.target.error);
  });
}

export async function countBackOfficeDocs() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_BO, "readonly");
    const req = tx.objectStore(STORE_BO).count();
    tx.oncomplete = () => db.close();
    req.onsuccess = () => resolve(req.result || 0);
    req.onerror = (e) => reject(e.target.error);
  });
}

export async function removeBackOfficeDocs(client_uuids) {
  if (!client_uuids || client_uuids.length === 0) return;
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_BO, "readwrite");
    const store = tx.objectStore(STORE_BO);
    client_uuids.forEach((id) => store.delete(id));
    tx.oncomplete = () => {
      try { window.dispatchEvent(new CustomEvent("bo-queue-updated")); } catch (_e) { /* Queue deletion is complete; notification does not control persistence. */ }
      resolve();
    };
    tx.onerror = (e) => reject(e.target.error);
  });
}

export async function clearBackOfficeQueue() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_BO, "readwrite");
    tx.objectStore(STORE_BO).clear();
    tx.oncomplete = () => {
      try { window.dispatchEvent(new CustomEvent("bo-queue-updated")); } catch (_e) { /* Queue update is complete; notification does not control persistence. */ }
      resolve();
    };
    tx.onerror = (e) => reject(e.target.error);
  });
}
